import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import PurchaseOrders from "@/pages/PurchaseOrders";
import SalesOrders from "@/pages/SalesOrders";
import Ledgers from "@/pages/Ledgers";
import Stores from "@/pages/Stores";
import Shortages from "@/pages/Shortages";
import RateSheets from "@/pages/RateSheets";
import Costing from "@/pages/Costing";
import Operations from "@/pages/Operations";
import QualityChecks from "@/pages/QualityChecks";
import Registers from "@/pages/Registers";
import Gate from "@/pages/Gate";
import Assistant from "@/pages/Assistant";
import Maintenance from "@/pages/Maintenance";
import Printing from "@/pages/Printing";
import Guidelines from "@/pages/Guidelines";
import Finance from "@/pages/Finance";
import Team from "@/pages/Team";
import { Loader2 } from "lucide-react";

// Handles the Emergent Google OAuth return: {origin}/#session_id=...
function AuthCallback() {
  const { processSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    processSession(sid)
      .then(() => {
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      })
      .catch(() => navigate("/login", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

const page = (el, roles) => (
  <ProtectedRoute roles={roles}>
    <Layout>{el}</Layout>
  </ProtectedRoute>
);

function RootRoutes() {
  const location = useLocation();
  // Synchronous check during render (prevents race conditions) — read hash from useLocation.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={page(<Dashboard />)} />
      <Route path="/products" element={page(<Products />)} />
      <Route path="/purchase-orders" element={page(<PurchaseOrders />, ["admin", "manager", "store_keeper"])} />
      <Route path="/sales-orders" element={page(<SalesOrders />, ["admin", "manager", "store_keeper"])} />
      <Route path="/ledgers" element={page(<Ledgers />, ["admin", "manager", "store_keeper"])} />
      <Route path="/stores" element={page(<Stores />, ["admin", "manager", "store_keeper"])} />
      <Route path="/shortages" element={page(<Shortages />, ["admin", "manager", "store_keeper"])} />
      <Route path="/rate-sheets" element={page(<RateSheets />, ["admin", "manager"])} />
      <Route path="/costing" element={page(<Costing />, ["admin", "manager"])} />
      <Route path="/finance" element={page(<Finance />, ["admin", "manager"])} />
      <Route path="/operations" element={page(<Operations />)} />
      <Route path="/quality-checks" element={page(<QualityChecks />)} />
      <Route path="/registers" element={page(<Registers />, ["admin", "manager", "store_keeper"])} />
      <Route path="/gate" element={page(<Gate />, ["admin", "manager", "store_keeper", "guard"])} />
      <Route path="/assistant" element={page(<Assistant />, ["admin", "manager"])} />
      <Route path="/maintenance" element={page(<Maintenance />)} />
      <Route path="/printing" element={page(<Printing />, ["admin", "manager", "store_keeper"])} />
      <Route path="/guidelines" element={page(<Guidelines />)} />
      <Route path="/team" element={page(<Team />, ["admin", "manager"])} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RootRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
